import { useCallback, useEffect, useRef, useState } from 'react';
import type HlsType from 'hls.js';
import { api, ApiError } from '../lib/api';
import { Button, Card, Alert } from '../components/ui';
import { RecordingTranscript, copyText, wallClock, whereLabel } from './RecordingTranscript';
import { GenerateDialog } from './RecordingGenerate';

/**
 * One recording: the player, the scrub bar, in and out points, download,
 * export, share and delete, and the transcript under it.
 *
 * Its own component so that the four-times-a-second time updates from the
 * video re-render this card alone, not the whole list of recordings beside
 * it, which is what made the page feel sluggish while playing.
 */

export interface Segment {
  tag: string;
  seconds: number;
  sprite?: boolean;
  spriteInterval?: number;
  spriteGrid?: [number, number];
  gapBefore?: number;
}

export interface Session {
  instanceId: string;
  source: string;
  session: string;
  seconds: number;
  segments: Segment[];
  prefix: string;
  expiresInDays: number | null;
  /** The machine's, encoder's or flow's name when it was recorded. */
  machineName?: string;
  origin?: { kind: string; id: string; name: string };
  /** Still being recorded. */
  recording?: boolean;
  /** A recorder's session set to be transcribed. */
  transcribe?: boolean;
  /** Parts were cut off permanently; headSeconds went from the front. */
  trimmed?: { at: string; by: string | null; headSeconds: number; tailSeconds: number };
  /** A recorder's session: six-second parts under proxy/. Absent on a machine's (five-minute parts). */
  archive?: { dir: string; ext: string };
  /** Where its transcript stands, as the hub lists it. */
  transcript?: { state: 'none' | 'working' | 'ready'; percent: number; lines: number };
  /** A name given on this page. */
  title?: string;
  /** Tags given on this page, lower case. */
  tags?: string[];
  /** Thumbnails the hub made for the whole recording (where the recorder made none per part). */
  sprites?: { interval: number; grid: [number, number]; sheets: number; cell: [number, number] };
  /** A still for the list, where there are no sprites yet: its S3 key. */
  poster?: string | null;
}

/** A speech-bubble mark: filled with lines when a transcript exists, hollow with a percentage while one is being made. */
export function TranscriptMark({ t, className = '' }: { t: Session['transcript']; className?: string }) {
  if (!t || t.state === 'none') return null;
  const working = t.state === 'working';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${working ? 'text-warning' : 'text-good'} ${className}`}
          title={working ? `Transcribing: ${t.percent}% done` : `Transcript: ${t.lines} lines`}>
      <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" className={working ? 'animate-pulse' : ''}>
        <path d="M2 2.5A1.5 1.5 0 0 1 3.5 1h9A1.5 1.5 0 0 1 14 2.5v7A1.5 1.5 0 0 1 12.5 11H7l-3.5 3v-3H3.5A1.5 1.5 0 0 1 2 9.5v-7z"
              fill={working ? 'none' : 'currentColor'} stroke="currentColor" strokeWidth="1.3" />
        {!working && <path d="M5 4.5h6M5 7h4" stroke="#0b0f14" strokeWidth="1.2" strokeLinecap="round" />}
      </svg>
      {working && <span>{t.percent}%</span>}
    </span>
  );
}

export interface Preset { id: string; label: string; perMinute: { usd: number; megabytes: number } }

export const fmt = (s: number) => {
  if (!isFinite(s) || s < 0) return '--:--';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return (h ? `${h}:${String(m).padStart(2, '0')}` : `${m}`) + `:${String(sec).padStart(2, '0')}`;
};

export const prettyStamp = (s: string) => s.replace('T', '  ').replace(/-(\d{2})-(\d{2})$/, ':$1:$2');

/** What a recording is called: its given title, else what made it (then the source, on a machine). */
export const madeBy = (s: Session) => s.origin && s.origin.kind !== 'machine' ? s.origin.name : (s.machineName ? `${s.machineName} · ${s.source}` : s.source);
export const sessionName = (s: Session) => s.title?.trim() || madeBy(s);

/** Segment start offsets, for mapping a bar position to a segment. */
export const offsets = (s: Session) => {
  let t = 0;
  return s.segments.map((seg) => {
    const entry = { ...seg, start: t };
    t += seg.seconds;
    return entry;
  });
};

const PHASES: Record<string, string> = {
  fetching: 'Fetching the parts',
  joining: 'Joining them into one file',
  uploading: 'Storing the file',
  compressing: 'Compressing',
};

export function RecordingPlayer({ session, presets, canDelete, ai, onShare, onShareTag, onDelete, onChanged }: {
  session: Session;
  presets: Preset[];
  canDelete: boolean;
  /** The hub has an Anthropic key: "AI Generate…" is offered on a transcribed recording. */
  ai: boolean;
  onShare: () => void;
  /** Share every recording carrying a tag, now and later. */
  onShareTag: (tag: string) => void;
  onDelete: () => void;
  /** The recording was changed on the server (trimmed, renamed, tagged): reload it. */
  onChanged: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<HlsType | null>(null);
  const [position, setPosition] = useState(0);
  const [markIn, setMarkIn] = useState<number | null>(null);
  const [copiedHere, setCopiedHere] = useState<string | null>(null);
  const [markOut, setMarkOut] = useState<number | null>(null);
  const [busy, setBusy] = useState<'download' | 'export' | 'trim' | 'transcribe' | null>(null);
  const [confirmTrim, setConfirmTrim] = useState(false);
  const [confirmTranscribe, setConfirmTranscribe] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Renaming and tagging: edits are sent as they are made; the server keeps them in the manifest.
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const [metaError, setMetaError] = useState('');

  async function saveMeta(patch: { title?: string | null; tags?: string[] }) {
    setMetaError('');
    try {
      await api.patch(`/recordings/${session.instanceId}/${session.session}`, patch);
      onChanged();
    } catch (err) {
      setMetaError(err instanceof Error ? err.message : 'Could not save.');
    }
  }
  // Whether a transcript exists, as the transcript panel finds out.
  const [hasTranscript, setHasTranscript] = useState<boolean | null>(null);
  const [progress, setProgress] = useState<{ text: string; percent: number | null } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'info' | 'good' | 'critical'; text: string } | null>(null);
  // '' is the original - cut by the hub, the picture copied. Anything else is a
  // smaller copy made by MediaConvert, which costs by the minute.
  const [quality, setQuality] = useState('');
  const duration = session.seconds;
  const rangeValid = markIn != null && markOut != null && markOut > markIn;

  // ---- Playback --------------------------------------------------------------

  useEffect(() => {
    setMarkIn(null);
    setMarkOut(null);
    setPosition(0);
    setNotice(null);
    setProgress(null);
    let cancelled = false;
    (async () => {
      // The server serves the playlist with every segment signed, at an address
      // of its own: a player fetches it with the session cookie like any request.
      const url = `/api/recordings/${session.instanceId}/${session.session}/playlist.m3u8`;
      hlsRef.current?.destroy();
      hlsRef.current = null;
      const video = videoRef.current;
      if (!video) return;

      // An iPhone or iPad plays HLS natively and does it better than any
      // script can; everything else gets hls.js, loaded on demand.
      const native = !!video.canPlayType('application/vnd.apple.mpegurl');
      const apple = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (native && apple) {
        video.removeAttribute('src');
        video.src = url;
        video.load();
        return;
      }
      const { default: Hls } = await import('hls.js');
      if (cancelled) return;
      if (Hls.isSupported()) {
        const hls = new Hls({
          lowLatencyMode: false,
          // Deeper buffers than the default: a recording is a file, and a
          // scrub forward lands on fetched data more often.
          maxBufferLength: 60,
          maxMaxBufferLength: 180,
          backBufferLength: 60,
          startFragPrefetch: true,
        });
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_e, d) => {
          if (d.fatal) setNotice({ tone: 'critical', text: `Playback error: ${d.details}` });
        });
        hlsRef.current = hls;
      } else if (native) {
        video.src = url;
      } else {
        setNotice({ tone: 'critical', text: 'This browser cannot play the recording here. Download it instead.' });
      }
    })();
    return () => { cancelled = true; };
    // Reloaded when the recording itself changes shape (a trim), not only when another is chosen.
  }, [session.instanceId, session.session, session.segments.length]);

  useEffect(() => () => hlsRef.current?.destroy(), []);

  const seek = useCallback((t: number) => { if (videoRef.current) videoRef.current.currentTime = Math.max(0, Math.min(duration, t)); }, [duration]);

  // I and O mark in and out, space plays and pauses, as in an edit suite.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
      const v = videoRef.current;
      if (!v) return;
      if (e.key === 'i' || e.key === 'I') setMarkIn(v.currentTime);
      else if (e.key === 'o' || e.key === 'O') setMarkOut(v.currentTime);
      else if (e.key === ' ' && document.activeElement !== v) { e.preventDefault(); if (v.paused) void v.play(); else v.pause(); }
      else return;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---- Download and export ----------------------------------------------------

  /**
   * Hand the URL to the browser as a download rather than a navigation. The
   * URL is signed with a Content-Disposition of attachment, so assigning
   * location saves the file and leaves the page alone.
   */
  const startDownload = (url: string) => { window.location.href = url; };

  const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

  /** Waits for a MediaConvert job (an export with a quality chosen), saying how far along it is. */
  async function compressed(jobId: string): Promise<string> {
    for (;;) {
      await wait(4000);
      const st = await api.get<{ status: string; percent: number; url?: string; message?: string }>(
        `/recordings/${session.instanceId}/${session.session}/compress/${jobId}`);
      if (st.status === 'done' && st.url) return st.url;
      if (st.status === 'failed') throw new Error(st.message ?? 'MediaConvert could not make the file.');
      setProgress({ text: 'Compressing', percent: st.percent });
    }
  }

  async function download() {
    setBusy('download');
    setNotice(null);
    setProgress({ text: 'Starting', percent: null });
    try {
      const made = await api.post<{ url?: string; jobId?: string; seconds: number; parts: number }>(
        `/recordings/${session.instanceId}/${session.session}/download`, quality ? { quality } : undefined);
      let url = made.url;
      let jobId = made.jobId;
      let outages = 0;
      let restarted = false;
      while (!url && jobId) {
        // Joining runs on the hub as a job; this asks how it is going until the file is there.
        await wait(3000);
        let st: { status: string; phase: string; percent: number; url?: string; message?: string };
        try {
          st = await api.get(`/recordings/${session.instanceId}/${session.session}/download/${jobId}`);
        } catch (err) {
          const status = err instanceof ApiError ? err.status : 0;
          // The hub is restarting (a deploy): Caddy answers 502 for a few seconds. Wait it out.
          if (status === 502 || status === 503 || status === 0) {
            if (++outages > 40) throw new Error('The hub stopped answering. Try the download again in a minute.');
            setProgress({ text: 'Waiting for the hub', percent: null });
            continue;
          }
          // The hub came back without the job: start it again, once. A join
          // that had finished is found cached and comes straight back.
          if (status === 404 && !restarted) {
            restarted = true;
            const again = await api.post<{ url?: string; jobId?: string }>(
              `/recordings/${session.instanceId}/${session.session}/download`, quality ? { quality } : undefined);
            url = again.url;
            jobId = again.jobId;
            setProgress({ text: 'Starting again after a hub restart', percent: null });
            continue;
          }
          throw err;
        }
        outages = 0;
        if (st.status === 'done' && st.url) { url = st.url; break; }
        if (st.status === 'failed') throw new Error(st.message ?? 'The join failed.');
        setProgress({ text: PHASES[st.phase] ?? st.phase, percent: st.percent });
      }
      if (!url) throw new Error('No file came back.');
      startDownload(url);
      setNotice({
        tone: 'good',
        text: `Download started: ${fmt(made.seconds)} in one file.` +
          (session.recording ? ' The recording is still running: it was joined up to now. Download again later for the rest.' : ''),
      });
    } catch (err) {
      setNotice({ tone: 'critical', text: err instanceof Error ? err.message : 'The download failed.' });
    } finally {
      setBusy(null);
      setProgress(null);
    }
  }

  async function exportClip(mode: 'fast' | 'accurate') {
    if (!rangeValid) return;
    setBusy('export');
    setNotice(null);
    setProgress({ text: mode === 'fast' ? 'Cutting on keyframes' : 'Re-encoding for a frame-exact cut (about as long as the clip)', percent: null });
    try {
      const made = await api.post<{ url?: string; jobId?: string }>(
        `/recordings/${session.instanceId}/${session.session}/export`,
        { startSec: markIn, endSec: markOut, mode, ...(quality ? { quality } : {}) },
      );
      startDownload(made.url ?? await compressed(made.jobId!));
      setNotice({ tone: 'good', text: `Clip ${fmt(markIn!)} to ${fmt(markOut!)} exported.` });
    } catch (err) {
      setNotice({ tone: 'critical', text: err instanceof ApiError || err instanceof Error ? err.message : 'The export failed.' });
    } finally {
      setBusy(null);
      setProgress(null);
    }
  }

  /**
   * Keep only the marked range, for good. Cuts on part boundaries (six seconds
   * on an encoder's recording, five minutes on a machine's): the part holding
   * the in point and the part holding the out point both stay.
   */
  const trimFrom = markIn ?? 0;
  const trimTo = markOut ?? duration;
  const partsBefore = offsets(session).filter((s) => s.start + s.seconds <= trimFrom).length;
  const partsAfter = offsets(session).filter((s) => s.start >= trimTo).length;
  const canTrim = canDelete && !session.recording && (markIn != null || markOut != null) && trimTo > trimFrom && partsBefore + partsAfter > 0;

  async function trim() {
    setBusy('trim');
    setConfirmTrim(false);
    setNotice(null);
    try {
      const r = await api.post<{ ok: boolean; changed: boolean; seconds: number; removed: number; kept: number }>(
        `/recordings/${session.instanceId}/${session.session}/trim`, { startSec: trimFrom, endSec: trimTo });
      setNotice({ tone: 'good', text: r.changed ? `Trimmed: ${r.removed} part${r.removed === 1 ? '' : 's'} removed, ${fmt(r.seconds)} kept.` : 'Nothing to remove at part boundaries.' });
      setMarkIn(null);
      setMarkOut(null);
      onChanged();
    } catch (err) {
      setNotice({ tone: 'critical', text: err instanceof Error ? err.message : 'The trim failed.' });
    } finally {
      setBusy(null);
    }
  }

  async function transcribe() {
    setBusy('transcribe');
    setConfirmTranscribe(false);
    setNotice(null);
    try {
      await api.post(`/recordings/${session.instanceId}/${session.session}/transcribe`);
      setNotice({ tone: 'good', text: 'Transcribing. The first lines appear under the player in a few minutes; a long recording takes a while.' });
      setHasTranscript(null);
    } catch (err) {
      setNotice({ tone: 'critical', text: err instanceof Error ? err.message : 'Could not start the transcript.' });
    } finally {
      setBusy(null);
    }
  }

  const days = session.expiresInDays;
  const preset = presets.find((x) => x.id === quality);
  const estimateSecs = rangeValid ? markOut! - markIn! : session.seconds;
  const partWord = session.archive ? '6 s' : '5 min';

  return (
    <Card
      id="recording-player"
      title={
        <div className="min-w-0 flex-1">
          {renaming ? (
            <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); setRenaming(false); void saveMeta({ title: titleDraft }); }}>
              <input autoFocus value={titleDraft} maxLength={120} onChange={(e) => setTitleDraft(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(false); }}
                     placeholder={madeBy(session)}
                     className="w-full max-w-md rounded border border-grid bg-plane px-2 py-1 text-sm text-ink" />
              <Button variant="primary" type="submit">Save</Button>
              <Button variant="ghost" type="button" onClick={() => setRenaming(false)}>Cancel</Button>
            </form>
          ) : (
            <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
              <span className="truncate">{sessionName(session)}</span>
              <button type="button" className="shrink-0 rounded px-1 text-xs font-normal text-ink-muted hover:bg-raised hover:text-ink" title="Rename"
                      onClick={() => { setTitleDraft(session.title ?? ''); setRenaming(true); }}>✎</button>
            </h2>
          )}
          <div className="text-xs text-ink-muted">
            {session.title && <span>{madeBy(session)} · </span>}
            {prettyStamp(session.session)} · {fmt(session.seconds)}
            {session.recording && <span className="ml-2" style={{ color: 'var(--color-critical)' }}>● recording</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {(session.tags ?? []).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full bg-raised px-2 py-0.5 text-xs text-ink-secondary">
                #{t}
                <button type="button" title={`Share everything tagged #${t}`} className="text-series-1 hover:underline" onClick={() => onShareTag(t)}>share</button>
                <button type="button" title="Remove this tag" className="text-ink-muted hover:text-ink" onClick={() => void saveMeta({ tags: (session.tags ?? []).filter((x) => x !== t) })}>×</button>
              </span>
            ))}
            <form className="inline-flex" onSubmit={(e) => { e.preventDefault(); if (tagDraft.trim()) { void saveMeta({ tags: [...(session.tags ?? []), tagDraft] }); setTagDraft(''); } }}>
              <input value={tagDraft} maxLength={40} onChange={(e) => setTagDraft(e.target.value)} placeholder="+ tag"
                     className="w-20 rounded border border-grid bg-plane px-1.5 py-0.5 text-xs text-ink placeholder:text-ink-muted focus:w-36" />
            </form>
            {metaError && <span className="text-xs text-bad">{metaError}</span>}
          </div>
        </div>
      }
      action={
        days != null && days <= 10 ? (
          <span className={`rounded px-2 py-0.5 text-xs ${days <= 3 ? 'bg-critical/15 text-bad' : 'bg-warning/15 text-warning'}`}>
            deleted in {days} day{days === 1 ? '' : 's'}
          </span>
        ) : undefined
      }
    >
      <div className="space-y-3 p-4">
        {/* aspect-video so the frame is the right shape before any metadata
            has loaded; without it the panel collapses and then jumps. */}
        <div className="relative">
          <video
            ref={videoRef}
            controls
            playsInline
            className="aspect-video w-full rounded bg-black"
            onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
          />
          {/* Where we are, as words: "Cam1: 2026-09-26, 2:59pm. 2:12:23". Over the
              top-left of the picture, clear of the native controls, as on the multiviewer. */}
          <button
            type="button"
            className="absolute left-2 top-2 rounded bg-black/60 px-2 py-0.5 text-[11px] text-ink-secondary hover:bg-black/80 hover:text-ink"
            title="Copy the recording's name, the date, the time of day and the time in the recording at the playhead"
            onClick={() => {
              void copyText(`${whereLabel(sessionName(session), wallClock(session.session, session.segments, session.trimmed?.headSeconds ?? 0, position))} ${fmt(position)}`)
                .then((ok) => { setCopiedHere(ok ? 'copied' : 'copy failed'); setTimeout(() => setCopiedHere(null), ok ? 1500 : 3000); });
            }}
          >
            {copiedHere ?? 'Copy'}
          </button>
        </div>

        {session.transcript?.state === 'working' && (
          <div className="-mt-2 space-y-1">
            <div className="flex items-center justify-between text-xs text-warning">
              <span>Transcribing… lines appear under the player as each few minutes is done.</span>
              <span>{session.transcript.percent}%</span>
            </div>
            <div className="h-1 overflow-hidden rounded bg-plane">
              <div className="h-full bg-warning" style={{ width: `${session.transcript.percent}%`, transition: 'width .5s' }} />
            </div>
          </div>
        )}

        {/* Scrub bar: the marked range, the gaps where the feed dropped (from
            the manifest - the media itself has no idea), and the playhead. */}
        <div
          className="relative h-8 cursor-pointer overflow-hidden rounded border border-grid bg-plane"
          title="Click to jump"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            seek(((e.clientX - r.left) / r.width) * duration);
          }}
        >
          <div className="absolute inset-y-0 left-0 bg-series-1/25" style={{ width: `${(position / duration) * 100}%` }} />
          {rangeValid && (
            <div className="absolute inset-y-0 bg-good/20"
                 style={{ left: `${(markIn! / duration) * 100}%`, width: `${((markOut! - markIn!) / duration) * 100}%` }} />
          )}
          {/* Each mark shows the moment it is set, not only once both exist. */}
          {markIn != null && (
            <div className="absolute inset-y-0 w-[2px] bg-good" style={{ left: `${(markIn / duration) * 100}%` }}>
              <span className="absolute left-0 top-0 rounded-br bg-good px-1 text-[10px] font-semibold leading-4 text-black">IN</span>
            </div>
          )}
          {markOut != null && (
            <div className="absolute inset-y-0 w-[2px] bg-good" style={{ left: `${(markOut / duration) * 100}%` }}>
              <span className="absolute right-0 top-0 rounded-bl bg-good px-1 text-[10px] font-semibold leading-4 text-black">OUT</span>
            </div>
          )}
          {offsets(session).filter((s) => s.gapBefore).map((s) => (
            <div key={s.tag} title={`feed dropped for ${s.gapBefore}s`} className="absolute inset-y-0 w-[3px] bg-bad"
                 style={{ left: `${(s.start / duration) * 100}%` }} />
          ))}
          <div className="absolute inset-y-0 w-[2px] bg-series-1" style={{ left: `${(position / duration) * 100}%` }} />
          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] tabular-nums text-ink-muted">
            {fmt(position)} / {fmt(duration)}
          </div>
        </div>

        {/* Marking a clip. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={markIn == null ? 'secondary' : 'ghost'} title="Mark the in point at the playhead (I)" onClick={() => setMarkIn(videoRef.current?.currentTime ?? 0)}>
            {markIn == null ? 'Set in' : `In ${fmt(markIn)}`}
          </Button>
          <Button variant={markOut == null ? 'secondary' : 'ghost'} title="Mark the out point at the playhead (O)" onClick={() => setMarkOut(videoRef.current?.currentTime ?? 0)}>
            {markOut == null ? 'Set out' : `Out ${fmt(markOut)}`}
          </Button>
          {markIn != null && <Button variant="ghost" title="Jump to the in point" onClick={() => seek(markIn)}>⇤</Button>}
          {markOut != null && <Button variant="ghost" title="Jump to the out point" onClick={() => seek(markOut)}>⇥</Button>}
          {(markIn != null || markOut != null) && (
            <Button variant="ghost" onClick={() => { setMarkIn(null); setMarkOut(null); }}>Clear</Button>
          )}
          <span className="text-xs text-ink-muted">
            {rangeValid
              ? `${fmt(markOut! - markIn!)} marked`
              : markIn != null && markOut != null
                ? 'The out point is before the in point.'
                : markIn != null
                  ? 'Now set an out point (O).'
                  : markOut != null
                    ? 'Now set an in point (I).'
                    : 'Play to a moment and press I for in, O for out. Space plays and pauses.'}
          </span>
        </div>

        {/* What to do with it. */}
        <div className="flex flex-wrap items-center gap-2 border-t border-grid pt-3">
          <Button variant="primary" disabled={busy !== null} loading={busy === 'download'} onClick={() => void download()}
                  title="The whole recording as one MP4, every audio pair kept. Joined once on the hub, then cached.">
            {busy === 'download' ? 'Preparing…' : 'Download recording'}
          </Button>
          <Button disabled={!rangeValid || busy !== null} loading={busy === 'export'} onClick={() => void exportClip('fast')}
                  title="Cuts on keyframes: near-instant, accurate to about a second">
            Export clip
          </Button>
          <Button variant="ghost" disabled={!rangeValid || busy !== null} onClick={() => void exportClip('accurate')}
                  title="Re-encodes to land on the exact frame; takes about as long as the clip">
            Frame-exact
          </Button>
          {presets.length > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-ink-muted" title="A smaller copy made by MediaConvert, which costs by the minute. The original is free and keeps every audio pair.">
              Size
              <select className="rounded border border-grid bg-plane px-1.5 py-1.5 text-xs text-ink" value={quality} onChange={(e) => setQuality(e.target.value)}>
                <option value="">Original</option>
                {presets.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
              {preset && (
                <span>
                  about {Math.max(1, Math.round(preset.perMinute.megabytes * estimateSecs / 60))} MB · US${Math.max(0.01, preset.perMinute.usd * estimateSecs / 60).toFixed(2)}
                </span>
              )}
            </label>
          )}
          {canDelete && !session.recording && (
            <Button variant="ghost" disabled={!canTrim || busy !== null} loading={busy === 'trim'} onClick={() => setConfirmTrim(true)}
                    title={`Permanently keep only the marked range (the in point to the end if only an in point is set). Cuts on ${partWord} part boundaries.`}>
              Trim…
            </Button>
          )}
          <div className="flex-1" />
          {ai && hasTranscript && (
            <Button variant="ghost" title="Write something from the transcript with Claude: a press release, a match report, key moments…" onClick={() => setGenerating(true)}>
              AI Generate…
            </Button>
          )}
          <Button variant="ghost" title="A link that lets someone without an account download this recording and its clips" onClick={onShare}>
            Share…
          </Button>
          {canDelete && (
            <Button variant="ghost" disabled={busy !== null} onClick={onDelete}>Delete</Button>
          )}
        </div>

        {/* No transcript: say so, and offer one. Hidden once words exist (the panel below shows them). */}
        {hasTranscript === false && !session.recording && !confirmTranscribe && session.transcript?.state !== 'working' && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
            {session.transcribe
              ? <span>Transcribing: lines appear here a few minutes at a time.</span>
              : (
                <>
                  <span>No transcript for this recording.</span>
                  <Button variant="ghost" disabled={busy !== null} loading={busy === 'transcribe'} onClick={() => setConfirmTranscribe(true)}
                          title="Transcribe what is said (Amazon Transcribe, about US$1.44 an hour)">
                    Transcribe…
                  </Button>
                </>
              )}
          </div>
        )}
        {confirmTrim && (
          <div className="space-y-2 rounded border border-grid bg-plane p-3">
            <Alert tone="critical">
              Permanently remove everything before {fmt(trimFrom)}{markOut != null ? ` and after ${fmt(trimTo)}` : ''}:
              {' '}{partsBefore + partsAfter} part{partsBefore + partsAfter === 1 ? '' : 's'} deleted, about {fmt(trimTo - trimFrom)} kept.
              Cuts land on {partWord} part boundaries, so a little more than the range stays. There is no backup and this cannot be undone.
            </Alert>
            <div className="flex gap-2">
              <Button variant="danger" onClick={() => void trim()}>Trim permanently</Button>
              <Button variant="ghost" onClick={() => setConfirmTrim(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {confirmTranscribe && (
          <div className="space-y-2 rounded border border-grid bg-plane p-3">
            <Alert tone="info">
              Transcribe {fmt(session.seconds)} of audio with Amazon Transcribe: about US${(Math.ceil(session.seconds / 60) * 0.024).toFixed(2)}.
              Lines appear under the player as each few minutes is done.
            </Alert>
            <div className="flex gap-2">
              <Button variant="primary" onClick={() => void transcribe()}>Transcribe</Button>
              <Button variant="ghost" onClick={() => setConfirmTranscribe(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {session.trimmed && !confirmTrim && (
          <div className="text-xs text-ink-muted">
            Trimmed {new Date(session.trimmed.at).toLocaleString()}: {fmt(session.trimmed.headSeconds)} cut from the start{session.trimmed.tailSeconds ? `, ${fmt(session.trimmed.tailSeconds)} from the end` : ''}.
          </div>
        )}

        {generating && <GenerateDialog session={session} open={generating} onClose={() => setGenerating(false)} />}

        {progress && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-ink-muted">
              <span>{progress.text}…</span>
              {progress.percent != null && <span>{progress.percent}%</span>}
            </div>
            <div className="h-1.5 overflow-hidden rounded bg-plane">
              <div className={`h-full bg-series-1 ${progress.percent == null ? 'w-1/3 animate-pulse' : ''}`}
                   style={progress.percent != null ? { width: `${progress.percent}%`, transition: 'width .5s' } : undefined} />
            </div>
            <div className="text-xs text-ink-muted">You can leave this page: the file is kept with the recording and the next download is instant.</div>
          </div>
        )}
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}

        <RecordingTranscript
          prefix={session.prefix}
          name={sessionName(session)}
          session={session.session}
          segments={session.segments}
          position={position}
          recording={!!session.recording || !!session.transcribe}
          headOffset={session.trimmed?.headSeconds ?? 0}
          onSeek={seek}
          onPresence={setHasTranscript}
        />
      </div>
    </Card>
  );
}
